import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Textarea } from './ui/textarea'
import { Badge } from './ui/badge'
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { revenueApi } from '../services/api'

const RevenueManager = ({ onClose }) => {
  const [revenues, setRevenues] = useState([])
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingRevenue, setEditingRevenue] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    type: 'planned',
    status: 'planned',
    date: new Date().toISOString().split('T')[0],
    customerId: '',
    recurringInterval: '',
    recurringEndDate: ''
  })

  useEffect(() => {
    loadRevenues()
    loadSummary()
  }, [])

  const loadRevenues = async () => {
    try {
      setLoading(true)
      // Use empty company filter to load all revenues for now
      const response = await revenueApi.getAll('')
      setRevenues(response.data || [])
    } catch (error) {
      console.error('Error loading revenues:', error)
      toast.error('Fehler beim Laden der Einnahmen')
    } finally {
      setLoading(false)
    }
  }

  const loadSummary = async () => {
    try {
      // Use empty company filter to load summary for now
      const response = await revenueApi.getSummary('')
      setSummary(response.data || {})
    } catch (error) {
      console.error('Error loading revenue summary:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title || !formData.amount || !formData.date) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus')
      return
    }

    try {
      setLoading(true)
      
      const revenueData = {
        ...formData,
        amount: parseFloat(formData.amount),
        companyId: 'default_company'
      }

      if (editingRevenue) {
        await revenueApi.update(editingRevenue.id, revenueData)
        toast.success('Einnahme erfolgreich aktualisiert')
      } else {
        await revenueApi.create(revenueData)
        toast.success('Einnahme erfolgreich angelegt')
      }
      
      resetForm()
      await loadRevenues()
      await loadSummary()
    } catch (error) {
      console.error('Error saving revenue:', error)
      toast.error('Fehler beim Speichern der Einnahme')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (revenue) => {
    setEditingRevenue(revenue)
    setFormData({
      title: revenue.title,
      description: revenue.description || '',
      amount: revenue.amount.toString(),
      type: revenue.type,
      status: revenue.status,
      date: revenue.date ? revenue.date.split('T')[0] : new Date().toISOString().split('T')[0],
      customerId: revenue.customerId || '',
      recurringInterval: revenue.recurringInterval || '',
      recurringEndDate: revenue.recurringEndDate ? revenue.recurringEndDate.split('T')[0] : ''
    })
    setShowForm(true)
  }

  const handleDelete = async (revenueId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Einnahme löschen möchten?')) {
      return
    }

    try {
      setLoading(true)
      await revenueApi.delete(revenueId)
      toast.success('Einnahme erfolgreich gelöscht')
      await loadRevenues()
      await loadSummary()
    } catch (error) {
      console.error('Error deleting revenue:', error)
      toast.error('Fehler beim Löschen der Einnahme')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      amount: '',
      type: 'planned',
      status: 'planned',
      date: new Date().toISOString().split('T')[0],
      customerId: '',
      recurringInterval: '',
      recurringEndDate: ''
    })
    setEditingRevenue(null)
    setShowForm(false)
  }

  const getStatusColor = (status) => {
    const colors = {
      'planned': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'invoiced': 'bg-purple-100 text-purple-800',
      'paid': 'bg-green-100 text-green-800',
      'overdue': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getTypeColor = (type) => {
    const colors = {
      'planned': 'bg-orange-100 text-orange-800',
      'one_time': 'bg-green-100 text-green-800',
      'recurring': 'bg-blue-100 text-blue-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-slate-800">
              💰 Einnahmen verwalten
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              €{(summary.total_planned || 0).toLocaleString()}
            </div>
            <div className="text-sm text-slate-600">Geplant</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              €{(summary.total_confirmed || 0).toLocaleString()}
            </div>
            <div className="text-sm text-slate-600">Bestätigt</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              €{(summary.monthly_recurring_revenue || 0).toLocaleString()}
            </div>
            <div className="text-sm text-slate-600">Monatlich wiederkehrend</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-800">
              {summary.total_count || 0}
            </div>
            <div className="text-sm text-slate-600">Gesamt Einträge</div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <Button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neue Einnahme
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingRevenue ? 'Einnahme bearbeiten' : 'Neue Einnahme anlegen'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Titel *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="z.B. Projekt XYZ"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Betrag (€) *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Typ
                  </label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({...formData, type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Typ wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Geplant</SelectItem>
                      <SelectItem value="one_time">Einmalig</SelectItem>
                      <SelectItem value="recurring">Wiederkehrend</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Status
                  </label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({...formData, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Status wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Geplant</SelectItem>
                      <SelectItem value="confirmed">Bestätigt</SelectItem>
                      <SelectItem value="invoiced">Berechnet</SelectItem>
                      <SelectItem value="paid">Bezahlt</SelectItem>
                      <SelectItem value="overdue">Überfällig</SelectItem>
                      <SelectItem value="cancelled">Storniert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Datum *
                  </label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Beschreibung
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Zusätzliche Informationen..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Speichern...' : editingRevenue ? 'Aktualisieren' : 'Anlegen'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Revenue List */}
      <Card>
        <CardHeader>
          <CardTitle>Einnahmen-Übersicht</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="text-slate-600">Lade Einnahmen...</div>
            </div>
          ) : revenues.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-slate-600">Noch keine Einnahmen angelegt</div>
            </div>
          ) : (
            <div className="space-y-4">
              {revenues.map((revenue) => (
                <div
                  key={revenue.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-semibold text-slate-800">{revenue.title}</h3>
                      <Badge className={getTypeColor(revenue.type)}>
                        {revenue.type === 'planned' ? 'Geplant' : 
                         revenue.type === 'one_time' ? 'Einmalig' : 'Wiederkehrend'}
                      </Badge>
                      <Badge className={getStatusColor(revenue.status)}>
                        {revenue.status === 'planned' ? 'Geplant' :
                         revenue.status === 'confirmed' ? 'Bestätigt' :
                         revenue.status === 'invoiced' ? 'Berechnet' :
                         revenue.status === 'paid' ? 'Bezahlt' :
                         revenue.status === 'overdue' ? 'Überfällig' : 'Storniert'}
                      </Badge>
                    </div>
                    {revenue.description && (
                      <p className="text-sm text-slate-600 mt-1">{revenue.description}</p>
                    )}
                    <div className="text-xs text-slate-500 mt-1">
                      {new Date(revenue.date).toLocaleDateString('de-DE')}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="font-bold text-lg text-slate-800">
                        €{revenue.amount.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(revenue)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(revenue.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default RevenueManager